import {Trans} from '@lingui/macro';
import React, {FC, useRef} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBox} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBox';
import {SimpleDropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {PopoverCoordinator} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {renderLocalApplicationChannelTypeContactType} from '../../../../../../helpers/localApplicationHelpers';
import {LocalApplicationChannelTypeContactTypesEnum} from '../../../../../../models/api/localApplicationChannelTypeModel';

/*
 * Constants.
 */

const dropdownWidth = 165;
const dropdownOptions = [
  LocalApplicationChannelTypeContactTypesEnum.EMAIL,
  LocalApplicationChannelTypeContactTypesEnum.PHONE,
  LocalApplicationChannelTypeContactTypesEnum.CUSTOM,
];

/*
 * Props.
 */

interface SettingsDevelopersChannelContactTypeInputProps {
  selectedContactType?: LocalApplicationChannelTypeContactTypesEnum;
  isDisabled: boolean;
  onContactTypeChange: HandlerOf<LocalApplicationChannelTypeContactTypesEnum>;
}

/*
 * Style.
 */

const StyledDropdownBox = styled(DropdownBox)`
  width: 171px;
`;

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledMessageWrapperDiv = styled.div`
  white-space: nowrap;
`;

/*
 * Component.
 */

export const SettingsDevelopersChannelContactTypeInput: FC<SettingsDevelopersChannelContactTypeInputProps> = (
  props,
) => {
  const {selectedContactType, isDisabled, onContactTypeChange} = props;
  const customAnchorRef = useRef<HTMLDivElement>(null);

  const renderDropdownValue = () => {
    if (!selectedContactType) {
      return (
        <StyledPlaceholderDiv>
          <Trans id="developers-application-contact-type-input-placeholder">Select contact type</Trans>
        </StyledPlaceholderDiv>
      );
    }
    return renderLocalApplicationChannelTypeContactType(selectedContactType);
  };

  return (
    <PopoverCoordinator
      isDisabled={isDisabled}
      renderButton={(hasHover) => (
        <StyledDropdownBox
          ref={customAnchorRef}
          hasHover={hasHover}
          isDisabled={isDisabled}
          noMargin
          boxStyles={{
            width: '138px',
            height: '30px',
            marginTop: 0,
          }}
          value={renderDropdownValue()}
          message={
            <StyledMessageWrapperDiv>
              <Trans id="developers-application-contact-type-input-message">
                The type of contacts your channel will communicate with.
              </Trans>
            </StyledMessageWrapperDiv>
          }
        />
      )}
      renderModal={(popoverProps) => (
        <SimpleDropdown
          anchor={customAnchorRef}
          onRequestClose={popoverProps.onRequestClose}
          width={dropdownWidth}
        >
          {dropdownOptions.map((option) => (
            <SimpleDropdownItem
              key={option}
              onSelect={() => onContactTypeChange(option)}
              isChecked={option === selectedContactType}
            >
              {renderLocalApplicationChannelTypeContactType(option)}
            </SimpleDropdownItem>
          ))}
        </SimpleDropdown>
      )}
    />
  );
};
